import { useState } from "react";
import { useLang } from "@/context/LanguageContext";
import { Share2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useSettings } from "@/context/SettingsContext";
import { makePanchangT } from "@/lib/panchangTamil";

// Generates branded 1080×1920 (9:16) portrait PNGs of the Panchangam and its
// sections, and shares (native sheet on mobile) or downloads them.
const W = 1080, H = 1920;
const DEVA = "'Noto Serif Devanagari','Tiro Devanagari Hindi',serif";
const SERIF = "Georgia,'Times New Roman',serif";
const PROMO_EN = "Get the most accurate Panchāṅgam on Dharmartha.org/panchangam";
const PROMO_HI = "सबसे सटीक पंचांगम् धर्मार्थ पर — Dharmartha.org/panchangam";
const PROMO_PREMIUM_EN = "Dharmartha Panchangam Premium · personalised for your birth & location";
const PROMO_PREMIUM_HI = "धर्मार्थ पंचांगम् प्रीमियम · आपके जन्म एवं स्थान हेतु वैयक्तिक";
const TAMIL = "'Noto Sans Tamil','Noto Serif Tamil','Latha',sans-serif";
const TELUGU = "'Noto Sans Telugu','Gautami',sans-serif";
const SCRIPT_FONT = { en: SERIF, hi: DEVA, ta: TAMIL, te: TELUGU };
// Card text for every site language, so a card never mixes scripts
// (Hindi card = Hindi, Tamil card = Tamil, ...). Unknown languages use English.
const I18N = {
  en: { summary: "Today's Panchāṅga", muhurta: "Muhūrta & Day Windows", choghadiya: "Choghaḍiyā",
    vara: "Vara", tithi: "Tithi", nakshatra: "Nakshatra", yoga: "Yoga", karana: "Karana", sunrise: "Sunrise", sunset: "Sunset",
    brahma: "Brahma Muhurta", abhijit: "Abhijit Muhurta", rahu: "Rahu Kalam", yama: "Yama Gandam", gulika: "Gulika Kalam",
    good: "● Auspicious", avoid: "● Best avoided", dayChogh: "Daytime Choghaḍiyā",
    promo: PROMO_EN, promoPremium: PROMO_PREMIUM_EN, badge: "PANCHANGAM  PREMIUM", share: "Share image", shareCard: "Share this card",
    preparing: "Preparing…", downloaded: "Image downloaded", failed: "Could not create the image." },
  hi: { summary: "आज का पंचांग", muhurta: "मुहूर्त एवं दिन-काल", choghadiya: "चौघड़िया",
    vara: "वार", tithi: "तिथि", nakshatra: "नक्षत्र", yoga: "योग", karana: "करण", sunrise: "सूर्योदय", sunset: "सूर्यास्त",
    brahma: "ब्रह्म मुहूर्त", abhijit: "अभिजित मुहूर्त", rahu: "राहु काल", yama: "यमगण्ड", gulika: "गुलिक काल",
    good: "● शुभ", avoid: "● वर्जित", dayChogh: "दिन का चौघड़िया",
    promo: PROMO_HI, promoPremium: PROMO_PREMIUM_HI, badge: "पंचांग प्रीमियम", share: "चित्र साझा करें", shareCard: "यह कार्ड साझा करें",
    preparing: "तैयार हो रहा है…", downloaded: "चित्र डाउनलोड हो गया", failed: "चित्र नहीं बन सका।" },
  ta: { summary: "இன்றைய பஞ்சாங்கம்", muhurta: "முகூர்த்தம் & நாள் காலங்கள்", choghadiya: "சௌகடியா",
    vara: "வாரம்", tithi: "திதி", nakshatra: "நட்சத்திரம்", yoga: "யோகம்", karana: "கரணம்", sunrise: "சூரிய உதயம்", sunset: "சூரிய அஸ்தமனம்",
    brahma: "பிரம்ம முகூர்த்தம்", abhijit: "அபிஜித் முகூர்த்தம்", rahu: "ராகு காலம்", yama: "எமகண்டம்", gulika: "குளிகை காலம்",
    good: "● சுபம்", avoid: "● தவிர்க்கவும்", dayChogh: "பகல் சௌகடியா",
    badge: "பஞ்சாங்கம் பிரீமியம்", promo: "மிகத் துல்லியமான பஞ்சாங்கம் — Dharmartha.org/panchangam", promoPremium: "தர்மார்த்தா பஞ்சாங்கம் பிரீமியம் — Dharmartha.org/panchangam",
    share: "படத்தைப் பகிரவும்", shareCard: "இந்த அட்டையைப் பகிரவும்",
    preparing: "தயாராகிறது…", downloaded: "படம் பதிவிறக்கப்பட்டது", failed: "படத்தை உருவாக்க முடியவில்லை." },
  te: { summary: "నేటి పంచాంగం", muhurta: "ముహూర్తాలు & దిన కాలాలు", choghadiya: "చౌఘడియ",
    vara: "వారం", tithi: "తిథి", nakshatra: "నక్షత్రం", yoga: "యోగం", karana: "కరణం", sunrise: "సూర్యోదయం", sunset: "సూర్యాస్తమయం",
    brahma: "బ్రహ్మ ముహూర్తం", abhijit: "అభిజిత్ ముహూర్తం", rahu: "రాహు కాలం", yama: "యమగండం", gulika: "గుళిక కాలం",
    good: "● శుభం", avoid: "● వర్జించండి", dayChogh: "పగటి చౌఘడియ",
    badge: "పంచాంగం ప్రీమియం", promo: "అత్యంత ఖచ్చితమైన పంచాంగం — Dharmartha.org/panchangam", promoPremium: "ధర్మార్థ పంచాంగం ప్రీమియం — Dharmartha.org/panchangam",
    share: "చిత్రాన్ని పంచుకోండి", shareCard: "ఈ కార్డును పంచుకోండి",
    preparing: "సిద్ధమవుతోంది…", downloaded: "చిత్రం డౌన్‌లోడ్ అయింది", failed: "చిత్రాన్ని సృష్టించలేకపోయాము." },
};
const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const MONTHS_L = {
  hi: ["जनवरी","फ़रवरी","मार्च","अप्रैल","मई","जून","जुलाई","अगस्त","सितंबर","अक्टूबर","नवंबर","दिसंबर"],
  ta: ["ஜனவரி","பிப்ரவரி","மார்ச்","ஏப்ரல்","மே","ஜூன்","ஜூலை","ஆகஸ்ட்","செப்டம்பர்","அக்டோபர்","நவம்பர்","டிசம்பர்"],
  te: ["జనవరి","ఫిబ్రవరి","మార్చి","ఏప్రిల్","మే","జూన్","జూలై","ఆగస్టు","సెప్టెంబర్","అక్టోబర్","నవంబర్","డిసెంబర్"],
};
const cardLang = (lang) => (I18N[lang] ? lang : "en");
const DAYS = ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"];
const DAYS_L = {
  hi: ["रविवार","सोमवार","मंगलवार","बुधवार","गुरुवार","शुक्रवार","शनिवार"],
  ta: ["ஞாயிறு","திங்கள்","செவ்வாய்","புதன்","வியாழன்","வெள்ளி","சனி"],
  te: ["ఆదివారం","సోమవారం","మంగళవారం","బుధవారం","గురువారం","శుక్రవారం","శనివారం"],
};
// Month (and weekday, for the Premium date label) in the card's language.
const localDate = (date, l) => {
  if (!date || !MONTHS_L[l]) return date || "";
  const d = MONTHS.reduce((s, m, i) => s.replace(m, MONTHS_L[l][i]), String(date));
  return DAYS.reduce((s, m, i) => s.replace(m, DAYS_L[l][i]), d);
};
// One-language value for a Panchang limb {en, deva}: English, Hindi (Devanagari),
// or the site's Panchang dictionary for other languages.
const limbText = (v, l, pt) => {
  if (!v) return "—";
  if (l === "en") return v.en || "—";
  if (l === "hi") return v.deva || pt(v.en) || "—";
  return pt(v.en) || v.en || "—";
};

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function drawHeader(ctx, data, title, premium, L) {
  const bg = ctx.createLinearGradient(0, 0, 0, H);
  bg.addColorStop(0, "#1b0f08"); bg.addColorStop(0.5, "#241209"); bg.addColorStop(1, "#160b05");
  ctx.fillStyle = bg; ctx.fillRect(0, 0, W, H);
  const glow = ctx.createRadialGradient(W / 2, 260, 40, W / 2, 260, 520);
  glow.addColorStop(0, "rgba(199,80,0,0.42)"); glow.addColorStop(1, "rgba(199,80,0,0)");
  ctx.fillStyle = glow; ctx.fillRect(0, 0, W, 760);
  ctx.strokeStyle = premium ? "rgba(233,162,59,0.7)" : "rgba(198,138,0,0.55)"; ctx.lineWidth = 3;
  roundRect(ctx, 48, 48, W - 96, H - 96, 28); ctx.stroke();

  const cx = W / 2;
  ctx.textAlign = "center";
  ctx.fillStyle = "#E9A23B"; ctx.font = `180px ${DEVA}`; ctx.fillText("ॐ", cx, 300);
  ctx.fillStyle = "#F3E7CE"; ctx.font = `64px ${DEVA}`; ctx.fillText("धर्मार्थ", cx, 400);
  ctx.fillStyle = "#C68A00"; ctx.font = `28px ${SERIF}`; ctx.fillText("D H A R M A R T H A", cx, 452);
  if (premium) {
    // Premium brand pill
    const t = L.t.badge || "PANCHANGAM  PREMIUM"; ctx.font = `26px ${L.l === "en" ? SERIF : L.font}`;
    const tw = ctx.measureText(t).width + 72;
    ctx.fillStyle = "rgba(233,162,59,0.14)"; ctx.strokeStyle = "rgba(233,162,59,0.6)"; ctx.lineWidth = 2;
    roundRect(ctx, cx - tw / 2, 476, tw, 46, 23); ctx.fill(); ctx.stroke();
    ctx.fillStyle = "#E9A23B"; ctx.fillText("✦ " + t, cx, 506);
  }
  ctx.fillStyle = "#F3E7CE"; ctx.font = `44px ${L.font}`; ctx.fillText(title, cx, 568);
  ctx.fillStyle = "#E7D4AE"; ctx.font = `38px ${L.font}`; ctx.fillText(localDate(data.date, L.l), cx, 628);
  if (data.place) { ctx.fillStyle = "#B8976B"; ctx.font = `32px ${SERIF}`; ctx.fillText(`\u{1F4CD} ${data.place}`, cx, 676); }
  ctx.strokeStyle = "rgba(198,138,0,0.4)"; ctx.lineWidth = 2;
  ctx.beginPath(); ctx.moveTo(180, 720); ctx.lineTo(W - 180, 720); ctx.stroke();
}

function drawFooter(ctx, L, premium) {
  ctx.textAlign = "center";
  // Prefilled promotional line (standardised across every card, follows site language).
  // Shrink to fit inside the card border — longer scripts (Tamil) would otherwise overflow.
  const promo = premium ? L.t.promoPremium : L.t.promo;
  let size = 30;
  do { ctx.font = `${size}px ${L.font}`; } while (ctx.measureText(promo).width > W - 200 && --size > 18);
  ctx.fillStyle = "#E9A23B";
  ctx.fillText(promo, W / 2, H - 120);
  ctx.fillStyle = "#8d7350"; ctx.font = `26px ${SERIF}`;
  ctx.fillText(premium ? "dharmartha.org/panchangam" : "dharmartha.org", W / 2, H - 78);
}

// A labelled row: label + value, both in the card's one language.
function drawRow(ctx, y, h, label, value, L, tint) {
  roundRect(ctx, 110, y, W - 220, h, 18);
  ctx.fillStyle = tint || "rgba(255,255,255,0.035)"; ctx.fill();
  ctx.strokeStyle = "rgba(198,138,0,0.22)"; ctx.lineWidth = 1.5; ctx.stroke();
  ctx.textAlign = "left";
  ctx.fillStyle = "#C68A00"; ctx.font = `26px ${L.font}`;
  ctx.fillText(L.l === "en" ? label.toUpperCase() : label, 150, y + 44);
  ctx.fillStyle = "#F3E7CE"; ctx.font = `44px ${L.font}`;
  ctx.fillText(value || "—", 150, y + 96, W - 300);
}

function bodySummary(ctx, data, L) {
  const tithi = [limbText(data.paksha, L.l, L.pt), limbText(data.tithi, L.l, L.pt)].filter((x) => x && x !== "—").join(" ") || "—";
  const rows = [
    [L.t.vara, limbText(data.vara, L.l, L.pt)],
    [L.t.tithi, tithi],
    [L.t.nakshatra, limbText(data.nakshatra, L.l, L.pt)],
    [L.t.yoga, limbText(data.yoga, L.l, L.pt)],
    [L.t.karana, limbText(data.karana, L.l, L.pt)],
  ];
  let y = 800; const rowH = 126;
  rows.forEach(([label, v]) => { drawRow(ctx, y, rowH, label, v, L); y += rowH + 16; });
  // Sun row
  y += 8; ctx.textAlign = "center";
  roundRect(ctx, 110, y, W - 220, 160, 18); ctx.fillStyle = "rgba(199,80,0,0.10)"; ctx.fill();
  ctx.strokeStyle = "rgba(199,80,0,0.3)"; ctx.stroke();
  const half = W / 2;
  ctx.fillStyle = "#C68A00"; ctx.font = `26px ${L.font}`;
  const up = (x) => (L.l === "en" ? x.toUpperCase() : x);
  ctx.fillText(up(L.t.sunrise), half - 210, y + 56); ctx.fillText(up(L.t.sunset), half + 210, y + 56);
  ctx.fillStyle = "#F3E7CE"; ctx.font = `48px ${SERIF}`;
  ctx.fillText(data.sunrise || "—", half - 210, y + 116); ctx.fillText(data.sunset || "—", half + 210, y + 116);
}

function bodyMuhurta(ctx, data, L) {
  const m = data.muhurta || {};
  const rows = [
    [L.t.brahma, m.brahma, "good"],
    [L.t.abhijit, m.abhijit, "good"],
    [L.t.rahu, m.rahu_kalam, "avoid"],
    [L.t.yama, m.yamaganda, "avoid"],
    [L.t.gulika, m.gulika_kalam, "avoid"],
  ];
  // legend
  ctx.textAlign = "center"; ctx.font = `28px ${L.font}`;
  ctx.fillStyle = "#9FCB8F"; ctx.fillText(L.t.good, W / 2 - 170, 788);
  ctx.fillStyle = "#E79A6B"; ctx.fillText(L.t.avoid, W / 2 + 170, 788);
  let y = 830; const rowH = 150;
  rows.forEach(([label, val, kind]) => {
    const tint = kind === "good" ? "rgba(140,190,120,0.08)" : "rgba(199,80,0,0.10)";
    roundRect(ctx, 110, y, W - 220, rowH - 20, 18); ctx.fillStyle = tint; ctx.fill();
    ctx.strokeStyle = kind === "good" ? "rgba(140,190,120,0.35)" : "rgba(199,80,0,0.3)"; ctx.lineWidth = 1.5; ctx.stroke();
    ctx.textAlign = "left";
    ctx.fillStyle = "#F3E7CE"; ctx.font = `40px ${L.font}`; ctx.fillText(label, 150, y + 76, 440);
    ctx.textAlign = "right";
    ctx.fillStyle = kind === "good" ? "#9FCB8F" : "#E79A6B"; ctx.font = `40px ${SERIF}`;
    ctx.fillText(val || "—", W - 150, y + 76);
    y += rowH;
  });
}

function bodyChoghadiya(ctx, data, L) {
  const list = ((data.choghadiya && data.choghadiya.day) || []).slice(0, 8);
  ctx.textAlign = "center"; ctx.fillStyle = "#B8976B"; ctx.font = `30px ${L.font}`;
  ctx.fillText(L.t.dayChogh, W / 2, 790);
  let y = 830; const rowH = 128;
  list.forEach((c) => {
    const good = c.nature === "good";
    roundRect(ctx, 110, y, W - 220, rowH - 18, 16);
    ctx.fillStyle = good ? "rgba(140,190,120,0.08)" : "rgba(199,80,0,0.09)"; ctx.fill();
    ctx.strokeStyle = good ? "rgba(140,190,120,0.3)" : "rgba(199,80,0,0.28)"; ctx.lineWidth = 1.5; ctx.stroke();
    const name = L.l === "en" ? (c.name || c.deva) : L.l === "hi" ? (c.deva || L.pt(c.name)) : L.pt(c.name || "");
    ctx.textAlign = "left";
    ctx.fillStyle = "#F3E7CE"; ctx.font = `40px ${L.font}`; ctx.fillText(name || "—", 150, y + 52);
    // The engine's note is English-only, so it is shown on English cards only.
    if (c.note && L.l === "en") { ctx.fillStyle = "#B8976B"; ctx.font = `26px ${SERIF}`; ctx.fillText(String(c.note).slice(0, 40), 150, y + 92); }
    ctx.textAlign = "right"; ctx.fillStyle = good ? "#9FCB8F" : "#E79A6B"; ctx.font = `40px ${SERIF}`;
    ctx.fillText(c.span || "", W - 150, y + 70);
    y += rowH;
  });
}

const VARIANTS = {
  summary: { body: bodySummary, labelKey: "share" },
  muhurta: { body: bodyMuhurta, labelKey: "shareCard" },
  choghadiya: { body: bodyChoghadiya, labelKey: "shareCard" },
};

async function buildImage(data, variant, L, premium) {
  const v = VARIANTS[variant] || VARIANTS.summary;
  const canvas = document.createElement("canvas");
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext("2d");
  // fonts.ready only covers fonts already used on the page; the card's script font
  // (Tamil/Telugu on Premium) may never have been requested, so load it explicitly
  // or the canvas falls back to a font that cannot shape the script.
  try {
    const sample = [L.t.summary, L.t.vara, L.t.badge].filter(Boolean).join(" ");
    const fams = String(L.font).split(",").map((x) => x.trim()).filter((x) => /^'/.test(x));
    await Promise.all(fams.flatMap((fam) => [document.fonts.load(`44px ${fam}`, sample), document.fonts.load(`600 44px ${fam}`, sample)]));
    await document.fonts.ready;
  } catch (e) {}
  drawHeader(ctx, data, L.t[variant] || L.t.summary, premium, L);
  v.body(ctx, data, L);
  drawFooter(ctx, L, premium);
  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), "image/png"));
}

export default function PanchangShareImage({ data, variant = "summary", label, className = "", premium = false, plain = false }) {
  const { lang } = useLang();
  const { panchang_tamil = {} } = useSettings() || {};
  const [busy, setBusy] = useState(false);
  if (!data) return null;
  const v = VARIANTS[variant] || VARIANTS.summary;
  const l = cardLang(lang);
  const L = { l, t: I18N[l], font: SCRIPT_FONT[l], pt: makePanchangT(l, panchang_tamil) };
  const title = L.t[variant] || L.t.summary;
  const promo = premium ? L.t.promoPremium : L.t.promo;

  // A friendly, prefilled caption so the share sheet (WhatsApp etc.) opens ready-to-send.
  const buildMessage = () => {
    const lines = [`${premium ? "✦ " : ""}${title} · ${data.place || ""} · ${localDate(data.date, l)}`.trim()];
    if (variant === "summary") {
      if (data.vara?.en) lines.push(`${L.t.vara}: ${limbText(data.vara, l, L.pt)}`);
      if (data.tithi?.en) lines.push(`${L.t.tithi}: ${[limbText(data.paksha, l, L.pt), limbText(data.tithi, l, L.pt)].filter((x) => x && x !== "—").join(" ")}`);
      if (data.nakshatra?.en) lines.push(`${L.t.nakshatra}: ${limbText(data.nakshatra, l, L.pt)}`);
      if (data.sunrise) lines.push(`${L.t.sunrise} ${data.sunrise}${data.sunset ? ` · ${L.t.sunset} ${data.sunset}` : ""}`);
    }
    lines.push("", promo);
    return lines.join("\n");
  };

  const run = async () => {
    setBusy(true);
    try {
      const blob = await buildImage(data, variant, L, premium);
      const file = new File([blob], `dharmartha-${premium ? "premium-" : ""}${variant}.png`, { type: "image/png" });
      const text = buildMessage();
      const canShareFiles = navigator.canShare && navigator.canShare({ files: [file] });
      if (navigator.share && canShareFiles) {
        await navigator.share({ files: [file], title, text });
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = `dharmartha-${premium ? "premium-" : ""}${variant}.png`;
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 2000);
        try { await navigator.clipboard?.writeText(text); } catch (e) {}
        toast.success(L.t.downloaded);
      }
    } catch (e) {
      if (e?.name !== "AbortError") toast.error(L.t.failed);
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      onClick={run}
      disabled={busy}
      data-testid={`panchang-share-${variant}`}
      className={`${plain
        ? "inline-flex items-center gap-1.5 text-sm font-medium text-ink-2 transition-colors hover:text-saffron disabled:opacity-60"
        : "inline-flex items-center gap-2 rounded-full border border-[var(--gold-muted)]/60 bg-surface px-4 py-2 text-sm text-ink-2 transition-colors hover:border-saffron hover:text-saffron disabled:opacity-60"} ${className}`}
    >
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Share2 className="h-4 w-4" />}
      {busy ? L.t.preparing : (label || L.t[v.labelKey])}
    </button>
  );
}
